import React from 'react';
import { useBranches } from '../../hooks/useBranches';

/**
 * Props for BranchSelect component
 */
export interface BranchSelectProps {
  /** Currently selected branch code */
  value: string;
  /** Callback when branch selection changes */
  onChange: (branchCode: string) => void;
  /** Include "All Branches" option for filters (value will be 'all') */
  includeAllOption?: boolean;
  /** Label for "All" option (default: "All Branches") */
  allOptionLabel?: string;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether field is required */
  required?: boolean;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Placeholder text when no selection */
  placeholder?: string;
}

/**
 * BranchSelect - Reusable branch dropdown component
 *
 * Uses the organization's BranchConfig from Firestore.
 * Branches are: Seaside, Tracy, Reno (from AppSheet Lists[Branches])
 *
 * @example
 * ```tsx
 * // Single branch selection in a form
 * <BranchSelect
 *   value={formData.branch}
 *   onChange={(branch) => setFormData({ ...formData, branch })}
 *   label="Branch"
 *   required
 * />
 *
 * // Branch filter with "All" option
 * <BranchSelect
 *   value={filterBranch}
 *   onChange={setFilterBranch}
 *   includeAllOption
 *   allOptionLabel="All Branches"
 * />
 * ```
 */
export function BranchSelect({
  value,
  onChange,
  includeAllOption = false,
  allOptionLabel = 'All Branches',
  label,
  error,
  required = false,
  disabled = false,
  className = '',
  placeholder = 'Select branch...',
}: BranchSelectProps) {
  const { branches, isLoading } = useBranches();

  const selectClasses = `
    w-full px-3 py-2 sm:px-4 sm:py-2.5
    text-sm sm:text-base
    border rounded-lg
    transition-colors duration-200
    bg-white
    disabled:bg-slate-100 disabled:cursor-not-allowed
    ${
      error
        ? 'border-danger-500 focus:border-danger-600 focus:ring-2 focus:ring-danger-200'
        : 'border-slate-300 focus:border-navy-500 focus:ring-2 focus:ring-navy-200'
    }
    ${className}
  `.trim();

  // Show skeleton while loading
  if (isLoading) {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {label}
            {required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}
        <div className="h-10 sm:h-11 bg-slate-200 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        className={selectClasses}
      >
        {/* Placeholder option */}
        {!includeAllOption && !value && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}

        {/* All option for filters */}
        {includeAllOption && (
          <option value="all">{allOptionLabel}</option>
        )}

        {/* Branch options from config */}
        {branches.map((branch) => (
          <option key={branch.branchId} value={branch.name}>
            {branch.name}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1.5 text-sm text-danger-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
